import { createApi } from '@reduxjs/toolkit/query/react'
import axiosBaseQuery from 'services/axiosBaseQuery'
import { DetailFieldType, GenericResponse, TableRequestV2, TableResponseV2 } from 'services/types'
import { RFIDChipCreateRequest, ExportRequest, MutationResponse } from './types'
import { sendExportNotification } from 'utils'
import { reportsApi } from 'services/ReportManagement/reports'
import { sendNotification } from 'components'
import { DropdownOptionTemplate } from 'types/common'
import { BaseColorType } from '@evrekadev/evreka-ui-components'

export const rfidChipsApi = createApi({
  reducerPath: 'rfidChipsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL + '/asset/'}`,
  }),
  tagTypes: ['RFIDChipList', 'RFIDChipDetail', 'RFIDChipStatuses'],
  endpoints: (builder) => ({
    getRFIDChipList: builder.query<
      TableResponseV2<any>,
      TableRequestV2<string> & { asset_id?: number }
    >({
      query: ({ client_id, asset_id, ...data }) => ({
        url: `clients/${client_id}/rfid_chip/list/`,
        method: 'POST',
        data: {
          ...data,
          ...(asset_id && { asset_id }),
        },
      }),
      providesTags: ['RFIDChipList'],
    }),
    exportRFIDChips: builder.mutation<MutationResponse, ExportRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/rfid_chip/export/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (error) {}
      },
    }),
    linkUnlinkRFIDChipToAsset: builder.mutation<
      MutationResponse,
      {
        client_id: number
        body: { rfid_chip_id: number; asset_id?: number; action: 'link' | 'unlink' }
      }
    >({
      query: ({ client_id, body }) => ({
        url: `clients/${client_id}/rfid_chip/link_unlink_asset/`,
        method: 'POST',
        data: body,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (error) {}
      },
      invalidatesTags: ['RFIDChipList', 'RFIDChipDetail'],
    }),
    createRFIDChip: builder.mutation<
      MutationResponse,
      {
        client_id: number
        body: RFIDChipCreateRequest
      }
    >({
      query: ({ client_id, body }) => ({
        url: `clients/${client_id}/rfid_chip/`,
        method: 'POST',
        data: body,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (error) {}
      },
      invalidatesTags: ['RFIDChipList'],
    }),
    updateRFIDChip: builder.mutation<
      MutationResponse,
      {
        client_id: number
        rfid_chip_id: string
        body: Omit<RFIDChipCreateRequest, 'chip_id'>
      }
    >({
      query: ({ client_id, rfid_chip_id, body }) => ({
        url: `clients/${client_id}/rfid_chip/${rfid_chip_id}/`,
        method: 'PUT',
        data: body,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (error) {}
      },
      invalidatesTags: ['RFIDChipList', 'RFIDChipDetail'],
    }),
    getRFIDChipDetail: builder.query<
      {
        id: DetailFieldType<string>
        chip_id: DetailFieldType<string>
        asset: DetailFieldType<string | null> & { id: number | null }
        status?: DetailFieldType<string> & { id: number }
        activation_date: DetailFieldType<string | null>
        expiry_date: DetailFieldType<string | null>
        created_at: DetailFieldType<string>
        updated_at?: DetailFieldType<string | null>
      },
      { client_id: number; rfid_chip_id: string }
    >({
      query: ({ client_id, rfid_chip_id }) => ({
        url: `clients/${client_id}/rfid_chip/${rfid_chip_id}/`,
        method: 'GET',
      }),
      providesTags: ['RFIDChipDetail'],
    }),
    searchRFIDChip: builder.mutation<
      GenericResponse & { rfid_chip_list: DropdownOptionTemplate<string> },
      { client_id: number; chip_id: string }
    >({
      query: ({ client_id, chip_id }) => ({
        url: `clients/${client_id}/rfid_chip/search/`,
        method: 'GET',
        params: { chip_id },
      }),
    }),
    getRFIDChipStatuses: builder.query<
      GenericResponse & {
        data: Array<DropdownOptionTemplate<number>[0] & { color: BaseColorType; linkable: boolean }>
      },
      { client_id: number }
    >({
      query: ({ client_id }) => ({
        url: `clients/${client_id}/rfid_chip/status/list/`,
        method: 'GET',
      }),
      providesTags: ['RFIDChipStatuses'],
    }),
    changeRFIDChipStatus: builder.mutation<
      MutationResponse,
      { client_id: number; rfid_chip_id: string; body: { status_id: number } }
    >({
      query: ({ client_id, rfid_chip_id, body }) => ({
        url: `clients/${client_id}/rfid_chip/${rfid_chip_id}/change_status/`,
        method: 'POST',
        data: body,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (error) {}
      },
      invalidatesTags: ['RFIDChipList', 'RFIDChipDetail'],
    }),
    deleteRFIDChip: builder.mutation<MutationResponse, { client_id: number; rfid_chip_id: string }>(
      {
        query: ({ client_id, rfid_chip_id }) => ({
          url: `clients/${client_id}/rfid_chip/${rfid_chip_id}/`,
          method: 'DELETE',
        }),
        onQueryStarted: async (_, { queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled
            sendNotification({
              type: 'success',
              toastContent: data.detail.message,
            })
          } catch (error) {}
        },
        invalidatesTags: ['RFIDChipList'],
      },
    ),
  }),
})

export const {
  useGetRFIDChipListQuery,
  useExportRFIDChipsMutation,
  useLinkUnlinkRFIDChipToAssetMutation,
  useCreateRFIDChipMutation,
  useUpdateRFIDChipMutation,
  useGetRFIDChipDetailQuery,
  useSearchRFIDChipMutation,
  useGetRFIDChipStatusesQuery,
  useChangeRFIDChipStatusMutation,
  useDeleteRFIDChipMutation,
} = rfidChipsApi
